import { useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import exportToCSV from "../components/exportToCSV";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

export async function loader() {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Response(
      JSON.stringify({
        message: "You must be logged in to access this resource.",
      }),
      { status: 401 }
    );
  }

  const response = await fetch(`${apiUrl}/ct/emails`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "X-App-Secret": process.env.REACT_APP_APP_SECRET,
    },
  });

  if (!response.ok) {
    throw new Response(JSON.stringify({ message: "Could not fetch emails." }), {
      status: response.status,
    });
  }

  const emails = await response.json();
  return emails;
}

export default function EmailsManagement() {
  const emails = useLoaderData();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    product_key: "",
    size_name: "",
    reason: "",
  });
  const [editRowId, setEditRowId] = useState(null);
  const [editableData, setEditableData] = useState({});

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const filteredEmails = emails.filter((email) =>
    Object.entries(filters).every(([key, value]) => {
      if (!value) return true; // Skip filtering if no value is set
      return String(email[key]).toLowerCase().includes(value.toLowerCase());
    })
  );

  const handleEditClick = (id, rowData) => {
    setEditRowId(id);
    setEditableData({
      email: rowData.email,
      reason: rowData.reason,
      status: rowData.status,
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditableData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveClick = async (id) => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("You must be logged in to save changes.");
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/ct/emails/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-App-Secret": process.env.REACT_APP_APP_SECRET,
        },
        body: JSON.stringify({
          email: editableData.email,
          reason: editableData.reason,
          status: editableData.status,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update email");
      }

      const updatedEmail = await response.json();
      alert(updatedEmail.message);

      // Exit edit mode
      setEditRowId(null);
      // full reload to refresh table
      navigate(".", { replace: true });
    } catch (err) {
      console.error("Error updating email:", err);
      alert("An error occurred while saving changes. Please try again.");
    }
  };

  const handleDeleteClick = async (id) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("You must be logged in to delete emails.");
        navigate("/login");
        return;
      }

      const response = await fetch(`${apiUrl}/ct/emails/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-App-Secret": process.env.REACT_APP_APP_SECRET,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete email");
      }

      // Update UI directly by reloading the page to fetch new data
      navigate(".", { replace: true });
    } catch (err) {
      console.error("Error deleting email:", err);
      alert("An error occurred while deleting the email.");
    }
  };

  const handleCancelClick = () => {
    setEditRowId(null);
    setEditableData({});
  };

  return (
    <div className="emails-container">
      <h1>Email Management</h1>
      <div className="filters">
        <label>
          Product Key:
          <input
            type="text"
            name="product_key"
            value={filters.product_key}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          Size:
          <input
            type="text"
            name="size_name"
            value={filters.size_name}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          Reason:
          <select
            name="reason"
            value={filters.reason}
            onChange={handleFilterChange}
          >
            <option value="">All</option>
            <option value="restock_notification">Restock Notification</option>
            <option value="newsletter">Newsletter</option>
          </select>
        </label>
      </div>

      <div className="export-button">
        <button
          onClick={() => exportToCSV(filteredEmails, "emails.csv")}
          className="export-btn"
        >
          Export CSV
        </button>
      </div>

      <table className="management-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Email</th>
            <th>Reason</th>
            <th>Product Key</th>
            <th>Name</th>
            <th>Size</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredEmails.map((email) => (
            <tr key={email.id}>
              {editRowId === email.id ? (
                <>
                  <td>{email.id}</td>
                  <td>
                    <input
                      type="text"
                      name="email"
                      value={editableData.email}
                      onChange={handleEditChange}
                    />
                  </td>
                  <td>
                    <select
                      name="reason"
                      value={editableData.reason}
                      onChange={handleEditChange}
                    >
                      <option value="restock_notification">
                        Restock Notification
                      </option>
                      <option value="newsletter">Newsletter</option>
                    </select>
                  </td>
                  <td>{email.product_key}</td> {/* Read-only */}
                  <td>{email.product_name}</td> {/* Read-only */}
                  <td>{email.size_name}</td> {/* Read-only */}
                  <td>
                    <select
                      name="status"
                      value={editableData.status}
                      onChange={handleEditChange}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </td>
                  <td>
                    <button
                      className="save-btn"
                      onClick={() => handleSaveClick(email.id)}
                    >
                      Save
                    </button>
                    {/* <button
                      className="delete-btn"
                      onClick={() => handleDeleteClick(email.id)}
                    >
                      Delete
                    </button> */}
                    <button className="cancel-btn" onClick={handleCancelClick}>
                      Cancel
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td>{email.id}</td>
                  <td>{email.email}</td>
                  <td>{email.reason}</td>
                  <td>{email.product_key}</td>
                  <td>{email.product_name}</td>
                  <td>{email.size_name}</td>
                  <td>{email.status}</td>
                  <td>
                    <button
                      className="edit-btn"
                      onClick={() => handleEditClick(email.id, email)}
                    >
                      Edit
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
